import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  Search,
  Plus,
  MoreVertical,
  FileText,
  AlertCircle,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Clock,
  Sparkles,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  Paperclip,
  X,
  User,
  Calendar,
  FileText as FileTextIcon,
  Check,
  Tag,
  TrendingUp,
  Lightbulb,
  ShieldCheck,
  Layers,
  FolderOpen,

} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { updatePolicyAIScoreCompliance, getProcedures, addProcedure, addAttachment, editProcedure, deleteProcedure, updatePolicyAIScore, getProceduresCompliance, addProcedureCompliance } from "../../store/actions/departmentActions";
import callOpenAIAPI, { callOpenAIAPIPolicyHealthWithFile, callOpenAIAPIPolicyHealthWithFileV2 } from "../../utils/openAI.js";

function formatDate(dateString, lng) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const locale = lng === 'tr' ? 'tr-TR' : 'en-US';
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

function HealthLevelIndicator({ status }) {
  const { t } = useTranslation();
  const getConfig = (status) => {
    if (status === 'excellent') return { color: 'bg-green-100 text-green-800', icon: Check, label: t('policyManagement.dominantHealth.excellent') };
    if (status === 'good') return { color: 'bg-blue-100 text-blue-800', icon: Check, label: t('policyManagement.dominantHealth.good') };
    if (status === 'warning') return { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: t('policyManagement.dominantHealth.warning') };
    if (status === 'critical') return { color: 'bg-red-100 text-red-800', icon: XCircle, label: t('policyManagement.dominantHealth.critical') };
    return { color: 'bg-gray-100 text-gray-800', icon: null, label: t('policyManagement.dominantHealth.unknown') };
  };

  if (!status) return null;

  const config = getConfig(status);
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {config.label}
    </span>
  );
}

function AIHealthScoreIndicator({ score }) {
  if (score === null || score === undefined) return null;

  const getConfig = (score) => {
    if (score >= 90) {
      return {
        color: "bg-green-500",
        track: "bg-green-100",
        icon: Sparkles,
        text: "text-green-700",
      };
    }
    if (score >= 70) {
      return {
        color: "bg-blue-500",
        track: "bg-blue-100",
        icon: Check,
        text: "text-blue-700",
      };
    }
    if (score >= 40) {
      return {
        color: "bg-orange-500",
        track: "bg-orange-100",
        icon: AlertTriangle,
        text: "text-orange-700",
      };
    }
    return {
      color: "bg-red-500",
      track: "bg-red-100",
      icon: XCircle,
      text: "text-red-700",
    };
  };

  const config = getConfig(score);
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-3 min-w-[120px]">
      {/* Icon */}
      <Icon className={`w-4 h-4 ${config.text}`} />

      {/* Progress bar */}
      <div className={`relative h-2 w-20 rounded-full ${config.track}`}>
        <div
          className={`h-2 rounded-full ${config.color}`}
          style={{ width: `${score}%` }}
        />
      </div>

      {/* Number */}
      <span className={`text-xs font-semibold ${config.text}`}>
        {score}
      </span>
    </div>
  );
}



const scoreToHealthLevel = (score) => {
  ////console.log("score ", score)
  if (score >= 90) return "excellent";
  if (score >= 70) return "good";
  if (score >= 40) return "warning";
  return "critical";
};

function decodeAndNormalizeFilename(encodedName) {
  return decodeFilename(encodedName).replace(/\s+/g, "_").split(".com/")[1];
}

function decodeFilename(encodedName) {
  if (!encodedName) return "";

  try {
    return decodeURIComponent(encodedName);
  } catch {
    return encodedName;
  }
}

function transformBackendPolicy(policy) {
  let complianceTags = [];
  if (policy.compliance_tags) {
    try {
      complianceTags = typeof policy.compliance_tags === 'string'
        ? JSON.parse(policy.compliance_tags)
        : policy.compliance_tags;
    } catch (e) {
      complianceTags = [];
    }
  }

  return {
    id: String(policy.policy_id),
    name: policy.policy_details || '',
    category: policy.category || '',
    status: policy.status || (policy.hide === 1 ? 'archived' : policy.hide === null ? 'active' : 'draft'),
    lastUpdated: policy.created_at ? new Date(policy.created_at).toISOString().split('T')[0] : '',
    complianceTags: complianceTags,
    healthLevel: policy.health_level || '',
    attachments: policy.policy_attachment ? [{
      id: `a${policy.policy_id}`,
      name: decodeAndNormalizeFilename(policy.policy_attachment) || 'attachment.pdf',
      size: '',
      uploadedDate: policy.created_at ? new Date(policy.created_at).toISOString().split('T')[0] : ''
    }] : [],
    policy_attachment: policy.policy_attachment || null,
    policy_no: policy.policy_no,
    department_id: policy.department_id,
    version: policy.version || '',
    description: policy.description || '',
    ai_health_score: policy.ai_health_score || 0,
    ai_clarity_score: policy.ai_clarity_score || 0,
    ai_completeness_score: policy.ai_completeness_score || 0,
    ai_compliance_score: policy.ai_compliance_score || 0,
    ai_recommendations: policy.ai_recommendations || {},
    signer: {
      name: policy.signer_name || '',
      role: policy.signer_role || '',
      signedDate: policy.signed_date || (policy.created_at ? new Date(policy.created_at).toISOString().split('T')[0] : ''),
      signature: policy.signer_signature || ''
    }
  };
}

function AddPolicyDialog({compliance_id, getProceduresComplianceFN,  addProcedureComplianceFN, open, onOpenChange, department_id, addProcedureFN, editProcedureFN, updatePolicyAIScoreFN, updatePolicyAIScoreComplianceFN, addAttachmentFN, getProceduresFN, onSuccess, formData: initialFormData, editMode = false, policyId = null, isComp }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    policyName: '',
    category: '',
    version: '1.0',
    status: 'draft',
    healthLevel: 'good',
    description: '',
    signerName: '',
    signerRole: '',
    signature: ''
  });
  const [complianceTags, setComplianceTags] = useState([]);
  const [complianceInput, setComplianceInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialFormData && open) {
      setFormData({
        policyName: initialFormData.policyName || '',
        category: initialFormData.category || '',
        version: initialFormData.version || '1.0',
        status: initialFormData.status || 'draft',
        healthLevel: initialFormData.healthLevel || 'good',
        description: initialFormData.description || '',
        signerName: initialFormData.signerName || '',
        signerRole: initialFormData.signerRole || '',
        signature: initialFormData.signature || ''
      });
      if (initialFormData.complianceTags) {
        setComplianceTags(initialFormData.complianceTags);
      }
    } else if (!open) {
      setFormData({
        policyName: '',
        category: '',
        version: '1.0',
        status: 'draft',
        healthLevel: 'good',
        description: '',
        signerName: '',
        signerRole: '',
        signature: ''
      });
      setComplianceTags([]);
    }
  }, [initialFormData, open]);

  const handleSubmit = async () => {
    if (!formData.policyName) {
      alert(t('policyManagement.alerts.policyNameRequired'));
      return;
    }
    if (!formData.signerName || !formData.signerRole || !formData.signature) {
      alert(t('policyManagement.alerts.signerRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const signedDate = new Date().toISOString().split('T')[0];
      const formDataToPass = {
        details: formData.policyName.trim(),
        policyName: formData.policyName.trim(),
        category: formData.category,
        status: formData.status,
        healthLevel: formData.healthLevel,
        complianceTags: complianceTags,
        version: formData.version,
        description: formData.description,
        signer: {
          name: formData.signerName,
          role: formData.signerRole,
          signature: formData.signature,
          signedDate: signedDate
        },
        signerName: formData.signerName,
        signerRole: formData.signerRole,
        signature: formData.signature,
        signedDate: signedDate
      };
      let createdPolicyId;
      if (editMode && policyId && editProcedureFN) {
        const policyData = {
          policy_details: formData.policyName.trim(),
          name: formData.policyName.trim(),
          policyName: formData.policyName.trim(),
          category: formData.category,
          version: formData.version,
          status: formData.status,
          healthLevel: formData.healthLevel,
          description: formData.description,
          compliance_tags: complianceTags,
          signer: {
            name: formData.signerName,
            role: formData.signerRole,
            signature: formData.signature,
            signedDate: new Date().toISOString().split('T')[0]
          },
          signer_name: formData.signerName,
          signer_role: formData.signerRole,
          signer_signature: formData.signature,
          signed_date: new Date().toISOString().split('T')[0]
        };

        ////console.log("adding atatchent ", selectedFile)
        await addAttachmentFN(department_id, policyId, selectedFile)
        await editProcedureFN(department_id, policyId, policyData);

        if (onSuccess) {
          onSuccess({ ...formDataToPass, policyId: policyId });
        }

        if (getProceduresFN) {
          setTimeout(() => {
            if (isComp) { getProceduresComplianceFN(compliance_id) } else {
              getProceduresFN(department_id);
            }
          }, 200);
        }
      } else {
        let result;
        if (isComp) {
          result = await addProcedureComplianceFN(compliance_id, formDataToPass)
        } else {
          result = await addProcedureFN(department_id, formDataToPass)
        }

        createdPolicyId = result?.policy_id;

        if (selectedFile && createdPolicyId) {
          await addAttachmentFN(department_id, createdPolicyId, selectedFile);
        }

        let responseData = null;
        if (result && typeof result.then === 'function') {
          responseData = await result;
        } else {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        if (onSuccess) {
          onSuccess(formDataToPass);
        }

        if (getProceduresFN) {
          setTimeout(() => {
            if (isComp) { getProceduresComplianceFN(compliance_id) } else {
              getProceduresFN(department_id);
            }
          }, 200);
        }
      }

      const aiResponse = await callOpenAIAPIPolicyHealthWithFileV2(formData.description, complianceTags, selectedFile)
      ////console.log("AI HEALTH ", aiResponse)
      if (isComp) {
        await updatePolicyAIScoreComplianceFN(compliance_id, policyId ? policyId : createdPolicyId, aiResponse)
      }
      else {
        await updatePolicyAIScoreFN(department_id, policyId ? policyId : createdPolicyId, aiResponse);
      }

      if (isComp) { await getProceduresComplianceFN(compliance_id) } else {
              await getProceduresFN(department_id);
            }
      handleClose();
      setIsSubmitting(false);
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} policy:`, error);
      alert(editMode ? t('policyManagement.alerts.errorUpdating') : t('policyManagement.alerts.errorCreating'));
      setIsSubmitting(false);
    }

  };

  const handleClose = async () => {
    setFormData({
      policyName: '',
      category: '',
      version: '1.0',
      status: 'draft',
      healthLevel: 'good',
      description: '',
      signerName: '',
      signerRole: '',
      signature: ''
    });
    setComplianceTags([]);
    setComplianceInput('');
    setSelectedFile(null);
    if (onOpenChange) {
      onOpenChange(false);
    }

  };

  const handleAddCompliance = () => {
    if (complianceInput.trim()) {
      setComplianceTags([...complianceTags, complianceInput.trim()]);
      setComplianceInput('');
    }
  };

  const handleRemoveCompliance = (index) => {
    setComplianceTags(complianceTags.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editMode ? t('policyManagement.modal.editTitle') : t('policyManagement.modal.addTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('policyManagement.modal.basicInformation')}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="policyName">
                  {t('policyManagement.modal.policyName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="policyName"
                  placeholder={t('policyManagement.modal.policyNamePlaceholder')}
                  value={formData.policyName}
                  onChange={(e) => setFormData({ ...formData, policyName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">
                  {t('policyManagement.modal.category')}
                </Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder={t('policyManagement.modal.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Security">{t('policyManagement.categories.security')}</SelectItem>
                    <SelectItem value="HR">{t('policyManagement.categories.hr')}</SelectItem>
                    <SelectItem value="Finance">{t('policyManagement.categories.finance')}</SelectItem>
                    <SelectItem value="Compliance">{t('policyManagement.categories.compliance')}</SelectItem>
                    <SelectItem value="Operations">{t('policyManagement.categories.operations')}</SelectItem>
                    <SelectItem value="IT">{t('policyManagement.categories.it')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">{t('policyManagement.modal.version')}</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">{t('policyManagement.modal.status')}</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t('policyManagement.modal.statusDraft')}</SelectItem>
                    <SelectItem value="review">{t('policyManagement.modal.statusReview')}</SelectItem>
                    <SelectItem value="active">{t('policyManagement.modal.statusActive')}</SelectItem>
                    <SelectItem value="archived">{t('policyManagement.modal.statusArchived')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* <div className="space-y-2">
              <Label htmlFor="healthLevel">Health Level</Label>
              <Select value={formData.healthLevel} onValueChange={(value) => setFormData({ ...formData, healthLevel: value })}>
                <SelectTrigger id="healthLevel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            <div className="space-y-2">
              <Label htmlFor="description">{t('policyManagement.modal.description')}</Label>
              <Textarea
                id="description"
                placeholder={t('policyManagement.modal.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('policyManagement.modal.signerInformation')}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signerName">
                  {t('policyManagement.modal.signerName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="signerName"
                  placeholder={t('policyManagement.modal.signerNamePlaceholder')}
                  value={formData.signerName}
                  onChange={(e) => setFormData({ ...formData, signerName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signerRole">
                  {t('policyManagement.modal.signerRole')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="signerRole"
                  placeholder={t('policyManagement.modal.signerRolePlaceholder')}
                  value={formData.signerRole}
                  onChange={(e) => setFormData({ ...formData, signerRole: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="signature">
                {t('policyManagement.modal.signature')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="signature"
                placeholder={t('policyManagement.modal.signaturePlaceholder')}
                value={formData.signature}
                onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('policyManagement.modal.relatedCompliance')}</h3>
            <div className="flex gap-2">
              <Input
                placeholder={t('policyManagement.modal.compliancePlaceholder')}
                value={complianceInput}
                onChange={(e) => setComplianceInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCompliance();
                  }
                }}
              />
              <Button type="button" onClick={handleAddCompliance} variant="outline">
                {t('policyManagement.modal.addTag')}
              </Button>
            </div>
            {complianceTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {complianceTags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    {tag}
                    <button
                      onClick={() => handleRemoveCompliance(index)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('policyManagement.modal.attachments')}</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-600">{t('policyManagement.modal.uploadHint')}</span>
                {selectedFile && (
                  <span className="text-xs text-gray-500 mt-2">{selectedFile.name}</span>
                )}
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('policyManagement.modal.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {editMode ? t('policyManagement.modal.saveChanges') : t('policyManagement.modal.addPolicy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDetailDialog({ open, onOpenChange, policy, getStatusBadge, onEdit }) {
  const { t, i18n } = useTranslation();
  if (!policy) return null;

  const getHealthLevelConfig = (level) => {
    switch (level) {
      case 'excellent':
        return { color: 'bg-green-100 text-green-800 border-green-200', icon: Check, label: t('policyManagement.dominantHealth.excellent'), description: t('policyManagement.healthDescriptions.excellent') };
      case 'good':
        return { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Check, label: t('policyManagement.dominantHealth.good'), description: t('policyManagement.healthDescriptions.good') };
      case 'warning':
        return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertTriangle, label: t('policyManagement.dominantHealth.warning'), description: t('policyManagement.healthDescriptions.warning') };
      case 'critical':
        return { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: t('policyManagement.dominantHealth.critical'), description: t('policyManagement.healthDescriptions.critical') };
      default:
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: null, label: t('policyManagement.dominantHealth.unknown'), description: t('policyManagement.healthDescriptions.unknown') };
    }
  };

  const healthConfig = scoreToHealthLevel(policy.ai_health_score) ? getHealthLevelConfig(scoreToHealthLevel(policy.ai_health_score)) : null;
  const HealthIcon = healthConfig?.icon;

  const handleClose = () => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileTextIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-gray-900">{policy.name}</h2>
              {policy.version && <p className="text-gray-500">{t('policyManagement.detail.versionWithVersion', { version: policy.version })}</p>}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="mb-6">
            <h3 className="text-gray-900 mb-2">{t('policyManagement.detail.description')}</h3>
            <p className="text-gray-600">{policy.description || t('policyManagement.detail.noDescription')}</p>
          </div>

          {policy.complianceTags && policy.complianceTags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-gray-900 mb-3">{t('policyManagement.detail.relatedCompliance')}</h3>
              <div className="flex flex-wrap gap-2">
                {policy.complianceTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 border border-gray-200"
                  >
                    <Tag className="w-4 h-4 text-gray-400" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {healthConfig && (
            <div className="mb-6">
              <h3 className="text-gray-900 mb-3">{t('policyManagement.detail.policyHealth')}</h3>
              <div className={`p-4 rounded-lg border ${healthConfig.color}`}>
                <div className="flex items-center gap-2 mb-2">
                  {HealthIcon && <HealthIcon className="w-5 h-5" />}
                  <span>{healthConfig.label}</span>
                </div>
                <p className="text-gray-700">{healthConfig.description}</p>
              </div>
            </div>
          )}

          {policy.signer && (
            <div className="mb-6">
              <h3 className="text-gray-900 mb-3">{t('policyManagement.detail.policySigner')}</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">{t('policyManagement.detail.nameLabel')}</div>
                      <div className="text-gray-900">{policy.signer.name || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">{t('policyManagement.detail.roleLabel')}</div>
                      <div className="text-gray-900">{policy.signer.role || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">{t('policyManagement.detail.signedDate')}</div>
                      <div className="text-gray-900">{formatDate(policy.signer.signedDate, i18n.language) || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">{t('policyManagement.detail.signatureLabel')}</div>
                      <div className="text-gray-900 italic">{policy.signer.signature || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-gray-900 mb-3">{t('policyManagement.detail.attachmentsWithCount', { count: policy.attachments?.length || 0 })}</h3>
            {policy.attachments && policy.attachments.length > 0 ? (
              <div className="space-y-2">
                {policy.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileTextIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-gray-900">{attachment.name}</div>
                        <div className="text-gray-500">
                          {attachment.size ? `${attachment.size} • ` : ''}{t('policyManagement.detail.uploaded', { date: attachment.uploadedDate })}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(policy.policy_attachment, '_blank')}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">{t('policyManagement.detail.noAttachments')}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <div className="text-gray-500">{t('policyManagement.detail.category')}</div>
              <div className="text-gray-900">{policy.category || '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('policyManagement.detail.status')}</div>
              <div className="text-gray-900">{getStatusBadge(policy.status)}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('policyManagement.detail.lastUpdated')}</div>
              <div className="text-gray-900">{policy.lastUpdated ? formatDate(policy.lastUpdated, i18n.language) : '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">{t('policyManagement.detail.policyId')}</div>
              <div className="text-gray-900">{policy.id}</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('policyManagement.detail.close')}
          </button>
          <button
            onClick={() => {
              handleClose();
              if (onEdit) {
                onEdit(policy);
              }
            }}
            className="px-4 py-2 text-white bg-gray-700 hover:bg-gray-800 rounded-lg transition-colors"
          >
            {t('policyManagement.detail.editPolicy')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PolicyManagement({ setCurrentPage, compliance_id, getProceduresComplianceFN,  addProcedureComplianceFN, isComp, department_id, getProceduresFN, addProcedureFN, addAttachmentFN, editProcedureFN, deleteProcedureFN, compPolicies, policies: backendPolicies, updatePolicyAIScoreFN, updatePolicyAIScoreComplianceFN }) {
  const { t, i18n } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  

  const [isAiSuggestionsOpen, setIsAiSuggestionsOpen] = useState(false);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isComp && compliance_id) {
      getProceduresComplianceFN(compliance_id)
    }
    else {
      if (department_id) {
        getProceduresFN(department_id);
      }
    }
  }, [department_id, getProceduresFN, compliance_id, getProceduresComplianceFN]);

  useEffect(() => {
    //console.log("backendPolicies ", backendPolicies)
    if (backendPolicies && Array.isArray(backendPolicies)) {
      const transformed = backendPolicies.map(transformBackendPolicy);
      setPolicies(transformed);
    }
  }, [backendPolicies]);

  
  useEffect(() => {
    //console.log("compPolicies ", compPolicies)
    if (compPolicies && isComp){
       const transformed = compPolicies.map(transformBackendPolicy);
      setPolicies(transformed)
    }
    
  }, [compPolicies]);


  const filteredPolicies = useMemo(() => {
    ////console.log("policies ", policies)
    return policies.filter(policy => {
      const matchesSearch = policy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        policy.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || policy.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [policies, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const total = policies.length;
    const active = policies.filter(p => p.status === 'active').length;
    const needsReview = policies.filter(p => p.status === 'review').length;
    const healthCounts = {
      excellent: policies.filter(p => p.healthLevel === 'excellent').length,
      good: policies.filter(p => p.healthLevel === 'good').length,
      warning: policies.filter(p => p.healthLevel === 'warning').length,
      critical: policies.filter(p => p.healthLevel === 'critical').length
    };
    const dominantHealth = Object.entries(healthCounts).reduce((a, b) =>
      healthCounts[a[0]] > healthCounts[b[0]] ? a : b
    )[0];

    return { total, active, needsReview, dominantHealth };
  }, [policies]);

  const getStatusBadge = (status) => {
    const variants = {
      active: { color: 'bg-green-100 text-green-800', labelKey: 'policyManagement.filter.active' },
      draft: { color: 'bg-gray-100 text-gray-800', labelKey: 'policyManagement.filter.draft' },
      review: { color: 'bg-orange-100 text-orange-800', labelKey: 'policyManagement.filter.review' },
      archived: { color: 'bg-slate-100 text-slate-800', labelKey: 'policyManagement.filter.archived' }
    };
    const config = variants[status] || variants.active;
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {t(config.labelKey)}
      </span>
    );
  };

  const handleCreatePolicy = () => {
    setIsDialogOpen(true);
  };

  const handlePolicyCreated = (formData) => {
    if (isComp && compliance_id) { getProceduresComplianceFN(compliance_id) } else if (department_id) {
      getProceduresFN(department_id);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (deleteProcedureFN && department_id) {
      try {
        await deleteProcedureFN(department_id, policyId);
         if (isComp) { getProceduresComplianceFN(compliance_id) } else {
              getProceduresFN(department_id);
            }
      } catch (error) {
        console.error('Error deleting policy:', error);
        alert(t('policyManagement.alerts.errorDelete'));
      }
    }
  };



  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    setIsDetailDialogOpen(false);
    setIsDialogOpen(true);
  };

  const handlePolicyUpdated = (formData) => {
    if (isComp && compliance_id) { getProceduresComplianceFN(compliance_id) } else if (department_id) {
      getProceduresFN(department_id);
    }
  };

  const handleViewDocument = (policy) => {
    if (policy.policy_attachment) {
      window.open(policy.policy_attachment, '_blank');
    }
  };


  return (
    <div className="h-full min-h-0 flex flex-col bg-[#F8F8F8]">
      {/* scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className=" pb-40 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-semibold mb-2">{t('policyManagement.title')}</h1>
              <p className="text-muted-foreground">
                {t('policyManagement.subtitle')}
              </p>
            </div>
           <nav className="flex gap-2">
            <Button
              variant={ "default" }
              onClick={() => setCurrentPage("policies")}
            >
              <FileText className="mr-2 h-4 w-4" />
              {t('policyManagement.tabs.policyManagement')}
            </Button>

            <Button
              variant={"ghost"}
              onClick={() => setCurrentPage("files")}
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              {t('policyManagement.tabs.fileManagement')}
            </Button>
          </nav>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">{t('policyManagement.summary.totalPolicies')}</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl">{stats.total}</div>
                <p className="text-xs text-muted-foreground mt-1">{t('policyManagement.summary.acrossCategories')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">{t('policyManagement.summary.activePolicies')}</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl">{stats.active}</div>
                <p className="text-xs text-muted-foreground mt-1">{t('policyManagement.summary.currentlyEnforced')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">{t('policyManagement.summary.needsReview')}</CardTitle>
                <Clock className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl">{stats.needsReview}</div>
                <p className="text-xs text-muted-foreground mt-1">{t('policyManagement.summary.pendingReview')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">{t('policyManagement.summary.avgHealthLevel')}</CardTitle>
                <Sparkles className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl capitalize">
                  {stats.dominantHealth
                    ? t(`policyManagement.dominantHealth.${stats.dominantHealth}`)
                    : t('policyManagement.dominantHealth.good')}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('policyManagement.summary.mostCommonStatus')}</p>
              </CardContent>
            </Card>
          </div>
          <Card>
       <CardHeader className="flex items-center py-3">
  <div className="flex items-center justify-between w-full gap-2">

 {/* LEFT / CENTER: Search + Status */}
<div className="flex items-center gap-2 flex-1">
  <div className="relative flex-1">
    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
    <Input
      placeholder={t('policyManagement.searchPlaceholder')}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      className="pl-9 h-9 w-full text-sm"
    />
  </div>

  <Select value={statusFilter} onValueChange={setStatusFilter}>
      <SelectTrigger className="w-[170px] h-9 text-sm">
      <SelectValue placeholder={t('policyManagement.filter.statusPlaceholder')} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{t('policyManagement.filter.all')}</SelectItem>
      <SelectItem value="active">{t('policyManagement.filter.active')}</SelectItem>
      <SelectItem value="draft">{t('policyManagement.filter.draft')}</SelectItem>
      <SelectItem value="review">{t('policyManagement.filter.review')}</SelectItem>
      <SelectItem value="archived">{t('policyManagement.filter.archived')}</SelectItem>
    </SelectContent>
  </Select>
</div>


  {/* RIGHT: Actions */}
  <div className="flex gap-1.5 shrink-0">
    <Button variant="outline" size="sm" onClick={() => {}}>
      <Upload className="mr-1.5 h-4 w-4" />
      {t('policyManagement.upload')}
    </Button>
    <Button size="sm" onClick={handleCreatePolicy}>
      <Plus className="mr-1.5 h-4 w-4" />
      {t('policyManagement.create')}
    </Button>
  </div>

</div>


</CardHeader>



          </Card>


          <Card>
            <CardHeader>
              <CardTitle>{t('policyManagement.table.title')}</CardTitle>
              <CardDescription>
                {filteredPolicies.length === 1
                  ? t('policyManagement.table.policyFound', { count: filteredPolicies.length })
                  : t('policyManagement.table.policiesFound', { count: filteredPolicies.length })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="text-gray-700">{t('policyManagement.table.policyName')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.category')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.compliance')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.status')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.healthLevel')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.lastUpdated')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.attachments')}</TableHead>
                    <TableHead className="text-gray-700">{t('policyManagement.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPolicies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-gray-900 mb-2">{t('policyManagement.empty.title')}</h3>
                        <p className="text-gray-500">{t('policyManagement.empty.hint')}</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPolicies.map((policy) => {
                      return (
                        <TableRow
                          key={policy.id}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setSelectedPolicy(policy);
                            setIsDetailDialogOpen(true);
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="text-gray-900 font-medium break-words">{policy.name}</div>
                                {policy.version && (
                                  <div className="text-gray-500 text-sm">v{policy.version}</div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-700">{policy.category || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {policy.complianceTags && policy.complianceTags.slice(0, 2).map((comp, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200 text-xs"
                                >
                                  {comp}
                                </span>
                              ))}
                              {policy.complianceTags && policy.complianceTags.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200 text-xs">
                                  +{policy.complianceTags.length - 2}
                                </span>
                              )}
                              {(!policy.complianceTags || policy.complianceTags.length === 0) && (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(policy.status)}</TableCell>
                          <TableCell>
                            <AIHealthScoreIndicator score={policy.ai_health_score} />
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-700">{formatDate(policy.lastUpdated, i18n.language)}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-700">
                                {policy.policy_attachment ? '1' : '0'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();

                                setMenuPos({
                                  top: rect.bottom + 6,
                                  left: rect.right - 180, // menu width
                                });

                                setOpenMenuId(openMenuId === policy.id ? null : policy.id);
                              }}
                              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            {openMenuId === policy.id && (
                              <div
                                className="fixed w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-[9999]"
                                style={{ top: menuPos.top, left: menuPos.left }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex gap-2"
                                  onClick={() => {
                                    setSelectedPolicy(policy);
                                    ////console.log("policy ", policy)
                                    setIsDetailDialogOpen(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                  {t('policyManagement.rowMenu.viewDetails')}
                                </button>

                                <button
                                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex gap-2"
                                  onClick={() => {
                                    setSelectedPolicy(policy);
                                    ////console.log("policy ", policy)
                                    setIsAiSuggestionsOpen(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <Sparkles className="w-4 h-4 text-indigo-500" />
                                  {t('policyManagement.rowMenu.viewAiSuggestions')}
                                </button>

                                <button
                                  className="w-full px-4 py-2 text-sm text-left hover:bg-red-50 text-red-600 flex gap-2"
                                  onClick={() => {
                                    handleDeletePolicy(policy.id);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t('policyManagement.rowMenu.delete')}
                                </button>
                              </div>
                            )}
                          </TableCell>


                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <AddPolicyDialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setSelectedPolicy(null);
              }
            }}
            department_id={department_id}
            addProcedureFN={addProcedureFN}
            addProcedureComplianceFN={addProcedureComplianceFN}
            editProcedureFN={editProcedureFN}
            updatePolicyAIScoreFN={updatePolicyAIScoreFN}
            updatePolicyAIScoreComplianceFN = {updatePolicyAIScoreComplianceFN}
            addAttachmentFN={addAttachmentFN}
            getProceduresFN={getProceduresFN}
            getProceduresComplianceFN={getProceduresComplianceFN}
            onSuccess={selectedPolicy ? handlePolicyUpdated : handlePolicyCreated}
            formData={selectedPolicy ? {
              policyName: selectedPolicy.name,
              category: selectedPolicy.category,
              version: selectedPolicy.version,
              status: selectedPolicy.status,
              healthLevel: selectedPolicy.healthLevel,
              description: selectedPolicy.description,
              signerName: selectedPolicy.signer?.name || '',
              signerRole: selectedPolicy.signer?.role || '',
              signature: selectedPolicy.signer?.signature || '',
              complianceTags: selectedPolicy.complianceTags || []
            } : null}
            editMode={!!selectedPolicy}
            policyId={selectedPolicy?.id || null}
            isComp={isComp}
            compliance_id={compliance_id}
          />



          <PolicyDetailDialog
            open={isDetailDialogOpen}
            onOpenChange={setIsDetailDialogOpen}
            policy={selectedPolicy}
            getStatusBadge={getStatusBadge}
            onEdit={handleEditPolicy}
          />

          {isAiSuggestionsOpen && selectedPolicy && (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
              onClick={() => setIsAiSuggestionsOpen(false)}
            >
              <div
                className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100 relative">
                  <div className="text-[15px] font-semibold text-gray-900">
                    {selectedPolicy?.policy_details || selectedPolicy?.name || t('policyManagement.aiModal.fallbackTitle')}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('policyManagement.aiModal.subtitle')}
                  </div>

                  <button
                    className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
                    onClick={() => setIsAiSuggestionsOpen(false)}
                  >
                    ✕
                  </button>

                  {/* Score row */}
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <div className="w-28 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100, Number(selectedPolicy?.ai_health_score ?? 0))
                            )}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="text-sm font-semibold text-gray-900">
                      {Number(selectedPolicy?.ai_health_score ?? 0)}
                    </div>

                    <span className="px-2 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      {(() => {
                        const raw = (selectedPolicy?.health_level || 'excellent').toLowerCase();
                        const known = ['excellent', 'good', 'warning', 'critical', 'unknown'].includes(raw);
                        return known
                          ? t(`policyManagement.dominantHealth.${raw}`)
                          : selectedPolicy?.health_level
                            ? selectedPolicy.health_level.charAt(0).toUpperCase() + selectedPolicy.health_level.slice(1)
                            : t('policyManagement.dominantHealth.excellent');
                      })()}
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5 max-h-[75vh] overflow-y-auto space-y-4">
                  {/* Health Metrics */}
                  <div className="border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <TrendingUp className="w-4 h-4 text-indigo-600" />
                      <span>{t('policyManagement.aiModal.healthMetrics')}</span>
                    </div>

                    {[
                      { labelKey: "policyManagement.aiModal.metricCompliance", metricKey: "ai_compliance_score" },
                      { labelKey: "policyManagement.aiModal.metricCompleteness", metricKey: "ai_completeness_score" },
                      { labelKey: "policyManagement.aiModal.metricClarity", metricKey: "ai_clarity_score" },
                    ].map((m) => {
                      const val = Number(selectedPolicy?.[m.metricKey] ?? 0);
                      return (
                        <div key={m.metricKey} className="mt-4">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                            <div className="flex items-center gap-2">
                              {m.metricKey === "ai_compliance_score" && <ShieldCheck className="w-4 h-4 text-emerald-600" />}
                              {m.metricKey === "ai_completeness_score" && <Layers className="w-4 h-4 text-blue-600" />}
                              {m.metricKey === "ai_clarity_score" && <Eye className="w-4 h-4 text-purple-600" />}
                              <span>{t(m.labelKey)}</span>
                            </div>
                            <span className="text-gray-700 font-semibold">{val}%</span>
                          </div>

                          <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full bg-gray-900"
                              style={{ width: `${Math.max(0, Math.min(100, val))}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* AI Recommendations */}
                  <div className="border border-gray-200 rounded-2xl p-5">
                    {(() => {
                      let recs = selectedPolicy?.ai_recommendations;

                      // if DB returns string JSON, normalize here
                      if (typeof recs === "string") {
                        try { recs = JSON.parse(recs); } catch { recs = []; }
                      }
                      if (!Array.isArray(recs)) recs = [];

                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                              <Lightbulb className="w-4 h-4 text-amber-500" />
                              <span>{t('policyManagement.aiModal.recommendationsTitle', { count: recs.length })}</span>
                            </div>
                          </div>

                          <div className="mt-3 space-y-2">
                            {recs.length === 0 ? (
                              <div className="text-sm text-gray-500">{t('policyManagement.aiModal.noRecommendations')}</div>
                            ) : (
                              recs.map((rec, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="mt-0.5 text-indigo-500">✨</span>
                                  <span>{rec}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Policy Details */}
                  <div className="border border-gray-200 rounded-2xl p-5">
                    <div className="text-sm font-semibold text-gray-900">{t('policyManagement.aiModal.policyDetails')}</div>

                    <div className="mt-4 divide-y divide-gray-100 text-sm">
                      <div className="py-3 flex items-center justify-between">
                        <span className="text-gray-500">{t('policyManagement.aiModal.owner')}</span>
                        <span className="text-gray-800 font-medium">
                          {selectedPolicy?.signer?.name || "-"}
                        </span>
                      </div>

                      <div className="py-3 flex items-center justify-between">
                        <span className="text-gray-500">{t('policyManagement.aiModal.version')}</span>
                        <span className="text-gray-800 font-medium">
                          {selectedPolicy?.version || "-"}
                        </span>
                      </div>

                      <div className="py-3 flex items-center justify-between">
                        <span className="text-gray-500">{t('policyManagement.aiModal.relatedCompliance')}</span>
                        <span className="text-gray-800 font-medium">
                          {(() => {
                            let tags = selectedPolicy?.compliance_tags;
                            if (typeof tags === "string") {
                              try { tags = JSON.parse(tags); } catch { tags = []; }
                            }
                            if (!Array.isArray(tags) || tags.length === 0) return "-";
                            return (
                              <span className="flex flex-wrap justify-end gap-2">
                                {tags.map((tag) => (
                                  <span key={tag} className="px-2 py-0.5 rounded-full border border-gray-200 text-xs text-gray-700">
                                    {tag}
                                  </span>
                                ))}
                              </span>
                            );
                          })()}
                        </span>
                      </div>

                      <div className="py-3 flex items-center justify-between">
                        <span className="text-gray-500">{t('policyManagement.aiModal.signedDate')}</span>
                        <span className="text-gray-800 font-medium">
                          {selectedPolicy?.signed_date || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-[11px] text-gray-400 flex items-center justify-center gap-2 pt-1">
                    <span>✨</span>
                    <span>
                      {t('policyManagement.aiModal.analysisFooter', {
                        date: new Date().toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US'),
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
         <div className="h-16" />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const departmentData = state.department?.departments || [];
  const complianceData = state.department?.compliances
  const activeCompliance = state.compliance.activeComplianceId;
  const activeDepartmentId = state.department?.activeDepartmentId || state.department?.department_id;
  const activeDepartment = departmentData.find(d => d.department_id === activeDepartmentId);
  const policies = (activeDepartment?.policies || []) ;

  //console.log("policies ", policies)
  //console.log("CompPolices ", complianceData)
  return {
    policies: policies,
    compPolicies: complianceData,
    department_id: activeDepartmentId,
    compliance_id: activeCompliance,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getProceduresFN: (d) => dispatch(getProcedures(d)),
    getProceduresComplianceFN: (c) => dispatch(getProceduresCompliance(c)),
    addProcedureFN: (d, data) => dispatch(addProcedure(d, data)),
    addProcedureComplianceFN: (c, data) => dispatch(addProcedureCompliance(c, data)),
    addAttachmentFN: (d, p, f) => dispatch(addAttachment(d, p, f)),
    editProcedureFN: (d, p, data) => dispatch(editProcedure(d, p, data)),
    deleteProcedureFN: (d, p) => dispatch(deleteProcedure(d, p)),
    updatePolicyAIScoreFN: (d, p, s) => dispatch(updatePolicyAIScore(d, p, s)),
    updatePolicyAIScoreComplianceFN: (c, p, s) => dispatch(updatePolicyAIScoreCompliance(c, p, s))
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(PolicyManagement);
